import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { inventoryService } from '@/services';
import { InventoryListParams } from '@/types';
import { queryKeys } from '@/services/query-keys';
import { retryUnlessMissingState, useRefetchStatesOnMissingState } from './missing-state';
import { groupInventoryBySymbol } from '@/utils/inventory/data-transformers';

/** Same as useInventoryAll so both hooks share one query and one request. */
const serializedParamsKey = (params?: Omit<InventoryListParams, 'limit' | 'offset'>): string =>
  params ? JSON.stringify(params) : '';

export const useAvailabilityReport = (
  params?: Omit<InventoryListParams, 'limit' | 'offset'>, 
  count?: number,
  benchmarkFeeThreshold?: number,
  enabled: boolean = true
) => {
  const refetchStatesOnMissing = useRefetchStatesOnMissingState();
  const query = useQuery({
    queryKey: [...queryKeys.inventory.all, 'all', serializedParamsKey(params), count],
    queryFn: () => refetchStatesOnMissing(() => inventoryService.getAllInventory(params, count)),
    retry: retryUnlessMissingState,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: enabled && count !== undefined && params?.state !== undefined,
  });

  const data = useMemo(() => {
    if (!query.data) return [];
    return groupInventoryBySymbol(query.data, benchmarkFeeThreshold);
  }, [query.data, benchmarkFeeThreshold]);

  return {
    ...query,
    data,
  };
};

